import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { simulationsApi } from "@/features/finances/api/simulations";
import type { OrderSimulationInput } from "@/features/finances/types/simulation";

const KEY = ["order-simulations"] as const;

export function useSimulations() {
  return useQuery({ queryKey: KEY, queryFn: simulationsApi.list });
}

export function useSimulation(id: string | null) {
  return useQuery({
    queryKey: [...KEY, id],
    queryFn: () => simulationsApi.get(id as string),
    enabled: !!id,
  });
}

export function useSimulationResult(id: string | null) {
  return useQuery({
    queryKey: [...KEY, id, "compute"],
    queryFn: () => simulationsApi.compute(id as string),
    enabled: !!id,
  });
}

export function useSimulationPreview() {
  return useMutation({
    mutationFn: (payload: OrderSimulationInput) => simulationsApi.preview(payload),
  });
}

export function useCreateSimulation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrderSimulationInput) => simulationsApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateSimulation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: OrderSimulationInput }) =>
      simulationsApi.update(id, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: [...KEY, vars.id] });
      qc.invalidateQueries({ queryKey: [...KEY, vars.id, "compute"] });
    },
  });
}

export function useDeleteSimulation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => simulationsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
